<script setup>
import { computed } from 'vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  endpoint: { type: Object, required: true },
});
defineEmits(['close']);

const baseHost = computed(() => {
  try { return new URL(props.endpoint.public_url).origin; } catch (e) { return 'https://staging.siteup.com.br'; }
});

const buttonHtml = computed(() => `<a href="${props.endpoint.public_url}" target="_blank" rel="noopener">
  Falar no WhatsApp
</a>`);

// Form HTML puro: o usuario poe a URL no action e o browser faz o resto.
// Apos POST: redirecionamos pro WhatsApp do lead com mensagem pronta.
const simpleFormHtml = computed(() => `<!-- Cole esse form no seu site (sem JavaScript) -->
<form action="${props.endpoint.public_url}" method="POST">
  <input name="nome"     type="text"  placeholder="Seu nome" required />
  <input name="telefone" type="tel"   placeholder="Seu WhatsApp (com DDD)" required />
  <input name="email"    type="email" placeholder="Seu e-mail" />

  <!-- Honeypot anti-spam (deixe oculto, nao mexa) -->
  <input name="website" style="display:none" tabindex="-1" autocomplete="off" />

  <button type="submit">Quero falar no WhatsApp</button>
</form>`);

// Versao avancada: captura UTMs/gclid/fbclid da URL atual da pagina via JS.
// Necessaria so se o lead chega na sua landing JA com ?gclid=... na URL e
// voce quer levar isso pro tracking. Pra anuncios Google Ads que apontam
// direto pro form, nao precisa.
const advancedFormHtml = computed(() => `<!-- Avancado: captura gclid/UTMs da URL antes de enviar -->
<form id="meu-form" action="${props.endpoint.public_url}" method="POST">
  <input name="nome"     type="text"  placeholder="Seu nome" required />
  <input name="telefone" type="tel"   placeholder="Seu WhatsApp" required />
  <input name="email"    type="email" placeholder="Seu e-mail" />

  <!-- Hidden fields preenchidos via JS abaixo -->
  <input type="hidden" name="gclid" />
  <input type="hidden" name="fbclid" />
  <input type="hidden" name="utm_source" />
  <input type="hidden" name="utm_medium" />
  <input type="hidden" name="utm_campaign" />

  <button type="submit">Enviar</button>
</form>

<script>
(function(){
  var p = new URLSearchParams(window.location.search);
  ['gclid','fbclid','utm_source','utm_medium','utm_campaign'].forEach(function(k){
    var el = document.querySelector('#meu-form input[name="' + k + '"]');
    if (el && p.get(k)) el.value = p.get(k);
  });
})();
</` + 'script>');

const curlExample = computed(() => `curl -X POST '${props.endpoint.public_url}' \
  -H 'Content-Type: application/json' \
  -d '{
    "name": "Joao da Silva",
    "phone": "+5551989342801",
    "email": "joao@exemplo.com",
    "gclid": "Cj0KCQ...",
    "utm_source": "google",
    "utm_campaign": "dedetizacao_2026"
  }'`);

function copyText(text) {
  navigator.clipboard.writeText(text).then(() => useAlert('Copiado!'));
}
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50" @click.self="$emit('close')">
    <div class="bg-n-background rounded-xl w-[90%] max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
      <header class="flex justify-between items-center px-6 py-4 border-b border-n-slate-4">
        <h3 class="text-base font-semibold text-n-slate-12">Como usar — {{ endpoint.name }}</h3>
        <button class="text-n-slate-11 hover:text-n-slate-12 text-xl" @click="$emit('close')">&times;</button>
      </header>

      <div class="px-6 py-5 overflow-y-auto flex-1">
        <section class="mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">URL publica</h4>
          <p class="text-xs text-n-slate-11 mb-2">Cole essa URL como Final URL do seu anuncio Google Ads ou no atributo href do botao do site.</p>
          <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
            <code class="block text-xs font-mono break-all text-n-slate-12">{{ endpoint.public_url }}</code>
            <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(endpoint.public_url)">Copiar</button>
          </div>
        </section>

        <section v-if="endpoint.endpoint_type === 'wa_redirect'" class="mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Botao HTML simples</h4>
          <p class="text-xs text-n-slate-11 mb-2">Cole no HTML pra ter um botao "Falar no WhatsApp".</p>
          <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
            <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ buttonHtml }}</pre>
            <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(buttonHtml)">Copiar</button>
          </div>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-1">Form HTML simples (recomendado)</h4>
          <p class="text-xs text-n-slate-11 mb-2">Cole esse codigo no seu site. <b>Sem JavaScript.</b> Quando o lead enviar, ele e redirecionado pro WhatsApp ja com mensagem pronta — voce nao faz nada manual.</p>
          <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
            <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ simpleFormHtml }}</pre>
            <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(simpleFormHtml)">Copiar</button>
          </div>
          <p class="text-xs text-n-slate-11 mt-2">
            Os nomes dos campos aceitam variacoes: <code>nome|name</code>, <code>telefone|phone|whatsapp|celular</code>, <code>email|e-mail</code>.
          </p>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <details>
            <summary class="text-sm font-semibold text-n-slate-12 cursor-pointer">Como usar em cada plataforma (WordPress, Framer, Webflow, etc)</summary>
            <div class="mt-3 space-y-3 text-xs text-n-slate-11">
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">WordPress (HTML puro num bloco)</p>
                <p>Vai em <b>Aparencia &rarr; Editor de tema</b> ou no editor da pagina, adiciona um bloco <b>HTML Personalizado</b> e cola o codigo do form HTML simples acima. Funciona em qualquer tema.</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">WordPress + WPForms / Gravity Forms / Elementor Pro</p>
                <p>Esses plugins paid suportam <b>Custom Action URL</b> ou <b>Webhook integration</b>. Configure pra apontar pra URL acima. O proprio plugin manda os dados como POST. Em alguns voce precisa configurar redirect manual apos sucesso.</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">Framer / Webflow / Wix / Squarespace</p>
                <p>Use um bloco <b>Embed Code</b> ou <b>HTML Custom</b> e cole o form HTML simples. O <code>action</code> aponta pra nossa URL e tudo funciona sem precisar configurar nada no painel da plataforma.</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">Site codado (HTML / Next.js / React / Astro / etc)</p>
                <p>Cola o form direto no JSX/HTML. Browser submitta nativo. Zero JavaScript necessario.</p>
              </div>
              <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3">
                <p class="font-semibold text-n-slate-12 mb-1">Aliases tolerantes nos campos</p>
                <p>Voce nao precisa renomear campos do form pra bater com nossa API. Aceitamos:</p>
                <ul class="mt-1 ml-4 list-disc">
                  <li><b>Nome</b>: nome | name | firstname | first_name | fullname | full_name</li>
                  <li><b>Telefone</b>: telefone | phone | whatsapp | celular | tel | mobile | cellphone | fone</li>
                  <li><b>Email</b>: email | e-mail | mail | correio</li>
                </ul>
              </div>
            </div>
          </details>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <details>
            <summary class="text-sm font-semibold text-n-slate-12 cursor-pointer">Avancado: capturar gclid/UTMs da URL da pagina</summary>
            <p class="text-xs text-n-slate-11 my-2">So precisa se a sua landing recebe trafego com <code>?gclid=...</code> na URL e voce quer atribuir a conversao no Google Ads. Se o anuncio Google Ads aponta direto pro form, NAO precisa disso.</p>
            <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
              <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ advancedFormHtml }}</pre>
              <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(advancedFormHtml)">Copiar</button>
            </div>
          </details>
        </section>

        <section v-if="endpoint.endpoint_type === 'webhook'" class="mb-6">
          <details>
            <summary class="text-sm font-semibold text-n-slate-12 cursor-pointer">Avancado: integracao via API (Tally / n8n / Zapier)</summary>
            <p class="text-xs text-n-slate-11 my-2">Pra quem quer enviar JSON via outro sistema (sem usar form HTML diretamente).</p>
            <div class="bg-n-slate-2 border border-n-slate-4 rounded p-3 relative">
              <pre class="text-xs font-mono whitespace-pre-wrap text-n-slate-12">{{ curlExample }}</pre>
              <button class="absolute top-2 right-2 text-xs px-2 py-1 rounded border border-n-slate-4 bg-n-background hover:bg-n-slate-3" @click="copyText(curlExample)">Copiar curl</button>
            </div>
          </details>
        </section>
      </div>

      <footer class="px-6 py-4 border-t border-n-slate-4 flex justify-end">
        <button class="px-4 py-2 rounded-md border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3" @click="$emit('close')">Fechar</button>
      </footer>
    </div>
  </div>
</template>
